/**
 * Hit Testing Module
 *
 * Standard hit testing utilities for canvas elements.
 * Provides batteries-included hit testing for nodes and edges,
 * including Bezier curve support for curved edges.
 *
 * Usage:
 * ```typescript
 * // Create a hit test function for CanvasEditor
 * const hitTest = createHitTest({
 *   getNodes: () => nodes,
 *   getEdges: () => edges,
 *   getZoom: () => camera.zoom,
 * });
 *
 * const editor = new CanvasEditor({
 *   hitTest,
 *   // ...
 * });
 * ```
 */

import type { Point, EditorNode, EditorEdge, HitTestResult, SelectionRect } from './types';

// =============================================================================
// Configuration
// =============================================================================

export interface HitTestConfig {
	/** Edge style: 'bezier' for curved edges, 'straight' for lines (default: 'bezier') */
	edgeStyle?: 'bezier' | 'straight';
	/** Base threshold in screen pixels for edge hit testing (default: 8) */
	edgeThreshold?: number;
}

export interface CreateHitTestOptions {
	/** Get all nodes */
	getNodes: () => EditorNode[];
	/** Get all edges */
	getEdges: () => EditorEdge[];
	/** Get current zoom level (for threshold adjustment) */
	getZoom?: () => number;
	/** Configuration options */
	config?: HitTestConfig;
}

// =============================================================================
// Node Hit Testing
// =============================================================================

/**
 * Get the center point of a node.
 * Assumes node.position is top-left corner.
 */
export function getNodeCenter(node: EditorNode): Point {
	return {
		x: node.position.x + node.size.width / 2,
		y: node.position.y + node.size.height / 2,
	};
}

/**
 * Hit test nodes at a point.
 * Returns the ID of the topmost node at the point, or null if none.
 * When nodes have z values, the node with the highest z is returned.
 * For nodes without z values (or equal z), later array position wins.
 */
export function hitTestNodes(nodes: EditorNode[], point: Point): string | null {
	let topNode: EditorNode | null = null;
	let topZ = -Infinity;
	let topIndex = -1;

	for (let i = 0; i < nodes.length; i++) {
		const node = nodes[i];
		if (
			point.x >= node.position.x &&
			point.x <= node.position.x + node.size.width &&
			point.y >= node.position.y &&
			point.y <= node.position.y + node.size.height
		) {
			const nodeZ = node.z ?? 0;
			// Pick node with higher z, or later index if z is equal
			if (nodeZ > topZ || (nodeZ === topZ && i > topIndex)) {
				topNode = node;
				topZ = nodeZ;
				topIndex = i;
			}
		}
	}
	return topNode?.id ?? null;
}

/**
 * Get node IDs that intersect with a selection rectangle.
 */
export function getNodesInRect(nodes: EditorNode[], rect: SelectionRect): string[] {
	const rectRight = rect.x + rect.width;
	const rectBottom = rect.y + rect.height;

	return nodes
		.filter((node) => {
			const nodeRight = node.position.x + node.size.width;
			const nodeBottom = node.position.y + node.size.height;

			// Check for intersection (not containment)
			return !(
				nodeRight < rect.x ||
				node.position.x > rectRight ||
				nodeBottom < rect.y ||
				node.position.y > rectBottom
			);
		})
		.map((node) => node.id);
}

// =============================================================================
// Bezier Edge Helpers
// =============================================================================

/**
 * Calculate control point offset for Bezier edge curve.
 * This creates a natural-looking curve that scales with edge length.
 */
export function getControlPointOffset(source: Point, target: Point): number {
	const dx = target.x - source.x;
	const dy = target.y - source.y;
	const distance = Math.sqrt(dx * dx + dy * dy);
	// Offset is 30% of distance, clamped between 30 and 150 pixels
	return Math.min(Math.max(distance * 0.3, 30), 150);
}

/**
 * Get the control point for a quadratic Bezier edge.
 * The curve direction depends on whether the edge is more horizontal or vertical.
 */
export function getEdgeControlPoint(source: Point, target: Point): Point {
	const dx = target.x - source.x;
	const dy = target.y - source.y;
	const offset = getControlPointOffset(source, target);

	if (Math.abs(dx) > Math.abs(dy)) {
		// More horizontal - curve perpendicular (up/down)
		return {
			x: (source.x + target.x) / 2,
			y: (source.y + target.y) / 2 - offset * Math.sign(dx),
		};
	} else {
		// More vertical - curve perpendicular (left/right)
		return {
			x: (source.x + target.x) / 2 + offset * Math.sign(dy),
			y: (source.y + target.y) / 2,
		};
	}
}

/**
 * Calculate minimum distance from a point to a quadratic Bezier curve.
 * Uses sampling for accuracy without complex math.
 */
export function distanceToBezierCurve(
	point: Point,
	start: Point,
	control: Point,
	end: Point,
	samples: number = 20
): number {
	let minDist = Infinity;

	for (let i = 0; i <= samples; i++) {
		const t = i / samples;
		const oneMinusT = 1 - t;

		// Quadratic Bezier: Q(t) = (1-t)² * P0 + 2*(1-t)*t * P1 + t² * P2
		const x = oneMinusT * oneMinusT * start.x + 2 * oneMinusT * t * control.x + t * t * end.x;
		const y = oneMinusT * oneMinusT * start.y + 2 * oneMinusT * t * control.y + t * t * end.y;

		const dist = Math.sqrt((point.x - x) ** 2 + (point.y - y) ** 2);
		if (dist < minDist) {
			minDist = dist;
		}
	}

	return minDist;
}

/**
 * Check if a Bezier curve intersects with a rectangle.
 * Uses sampling to check if any point on the curve is inside the rect.
 */
export function bezierIntersectsRect(
	start: Point,
	control: Point,
	end: Point,
	rect: SelectionRect,
	samples: number = 20
): boolean {
	const rectRight = rect.x + rect.width;
	const rectBottom = rect.y + rect.height;

	for (let i = 0; i <= samples; i++) {
		const t = i / samples;
		const oneMinusT = 1 - t;

		const x = oneMinusT * oneMinusT * start.x + 2 * oneMinusT * t * control.x + t * t * end.x;
		const y = oneMinusT * oneMinusT * start.y + 2 * oneMinusT * t * control.y + t * t * end.y;

		if (x >= rect.x && x <= rectRight && y >= rect.y && y <= rectBottom) {
			return true;
		}
	}

	return false;
}

// =============================================================================
// Straight Line Helpers
// =============================================================================

/**
 * Calculate distance from a point to a line segment.
 */
export function distanceToLineSegment(point: Point, start: Point, end: Point): number {
	const dx = end.x - start.x;
	const dy = end.y - start.y;
	const lengthSq = dx * dx + dy * dy;

	if (lengthSq === 0) {
		// Line segment is a point
		return Math.sqrt((point.x - start.x) ** 2 + (point.y - start.y) ** 2);
	}

	// Project point onto line, clamping to segment
	const t = Math.max(0, Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq));
	const projX = start.x + t * dx;
	const projY = start.y + t * dy;

	return Math.sqrt((point.x - projX) ** 2 + (point.y - projY) ** 2);
}

/**
 * Check if a line segment intersects with a rectangle.
 */
export function lineIntersectsRect(start: Point, end: Point, rect: SelectionRect): boolean {
	const rectRight = rect.x + rect.width;
	const rectBottom = rect.y + rect.height;

	// Check if either endpoint is inside the rect
	const startInside =
		start.x >= rect.x && start.x <= rectRight && start.y >= rect.y && start.y <= rectBottom;
	const endInside =
		end.x >= rect.x && end.x <= rectRight && end.y >= rect.y && end.y <= rectBottom;
	if (startInside || endInside) return true;

	// Check line-rect intersection using parametric line equation
	const dx = end.x - start.x;
	const dy = end.y - start.y;

	const tValues: number[] = [];

	if (dx !== 0) {
		tValues.push((rect.x - start.x) / dx); // left edge
		tValues.push((rectRight - start.x) / dx); // right edge
	}
	if (dy !== 0) {
		tValues.push((rect.y - start.y) / dy); // top edge
		tValues.push((rectBottom - start.y) / dy); // bottom edge
	}

	for (const t of tValues) {
		if (t >= 0 && t <= 1) {
			const x = start.x + t * dx;
			const y = start.y + t * dy;
			if (x >= rect.x && x <= rectRight && y >= rect.y && y <= rectBottom) {
				return true;
			}
		}
	}

	return false;
}

// =============================================================================
// Edge Hit Testing
// =============================================================================

/**
 * Hit test edges at a point.
 * Returns the ID of the closest edge within threshold, or null if none.
 *
 * @param edges - Array of edges to test
 * @param nodes - Array of nodes (needed to find edge endpoints)
 * @param point - The point to test
 * @param threshold - Maximum distance in world units to consider a hit
 * @param edgeStyle - 'bezier' for curved edges (default), 'straight' for lines
 */
export function hitTestEdges(
	edges: EditorEdge[],
	nodes: EditorNode[],
	point: Point,
	threshold: number = 8,
	edgeStyle: 'bezier' | 'straight' = 'bezier'
): string | null {
	const nodeMap = new Map(nodes.map((n) => [n.id, n]));

	for (let i = edges.length - 1; i >= 0; i--) {
		const edge = edges[i];
		const sourceNode = nodeMap.get(edge.sourceNodeId);
		const targetNode = nodeMap.get(edge.targetNodeId);

		if (!sourceNode || !targetNode) continue;

		const start = getNodeCenter(sourceNode);
		const end = getNodeCenter(targetNode);

		let distance: number;
		if (edgeStyle === 'bezier') {
			const control = getEdgeControlPoint(start, end);
			distance = distanceToBezierCurve(point, start, control, end);
		} else {
			distance = distanceToLineSegment(point, start, end);
		}

		if (distance <= threshold) {
			return edge.id;
		}
	}
	return null;
}

/**
 * Get edge IDs that intersect with a selection rectangle.
 *
 * @param edges - Array of edges to test
 * @param nodes - Array of nodes (needed to find edge endpoints)
 * @param rect - The selection rectangle
 * @param edgeStyle - 'bezier' for curved edges (default), 'straight' for lines
 */
export function getEdgesInRect(
	edges: EditorEdge[],
	nodes: EditorNode[],
	rect: SelectionRect,
	edgeStyle: 'bezier' | 'straight' = 'bezier'
): string[] {
	const nodeMap = new Map(nodes.map((n) => [n.id, n]));
	const result: string[] = [];

	for (const edge of edges) {
		const sourceNode = nodeMap.get(edge.sourceNodeId);
		const targetNode = nodeMap.get(edge.targetNodeId);

		if (!sourceNode || !targetNode) continue;

		const start = getNodeCenter(sourceNode);
		const end = getNodeCenter(targetNode);

		let intersects: boolean;
		if (edgeStyle === 'bezier') {
			const control = getEdgeControlPoint(start, end);
			intersects = bezierIntersectsRect(start, control, end, rect);
		} else {
			intersects = lineIntersectsRect(start, end, rect);
		}

		if (intersects) {
			result.push(edge.id);
		}
	}

	return result;
}

// =============================================================================
// Factory Function
// =============================================================================

/**
 * Create a hit test function for use with CanvasEditor.
 *
 * This is the recommended way to create hit testing for CanvasEditor.
 * It handles zoom-adjusted thresholds and provides sensible defaults.
 *
 * @example
 * ```typescript
 * const hitTest = createHitTest({
 *   getNodes: () => nodes,
 *   getEdges: () => edges,
 *   getZoom: () => camera.zoom,
 * });
 *
 * const editor = new CanvasEditor({
 *   hitTest,
 *   // ...
 * });
 * ```
 */
export function createHitTest(options: CreateHitTestOptions): (point: Point) => HitTestResult {
	const { getNodes, getEdges, getZoom, config = {} } = options;
	const { edgeStyle = 'bezier', edgeThreshold = 8 } = config;

	return (point: Point): HitTestResult => {
		const nodes = getNodes();
		const edges = getEdges();

		// Check nodes first (they're on top of edges)
		const nodeId = hitTestNodes(nodes, point);
		if (nodeId) {
			return { nodeId, edgeId: null, handleId: null, resizeHandleId: null };
		}

		// Adjust threshold based on zoom (8 screen pixels → world pixels)
		const zoom = getZoom?.() ?? 1;
		const worldThreshold = edgeThreshold / zoom;

		const edgeId = hitTestEdges(edges, nodes, point, worldThreshold, edgeStyle);
		return { nodeId: null, edgeId, handleId: null, resizeHandleId: null };
	};
}
